import { Rnd } from 'react-rnd';
import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { BoundingBoxHandles, SvgBoxMask } from '@/assets/svg/components';
import type { BoxDimensions, Dimensions } from '@/types/BoundingBox.types';
import {
  reverseScaleCoordinates,
  scaleCoordinates,
} from '@/utils/scaleCoordinates';
import styles from './SimilarProductImageItem.module.css';
import type { SimilarProductImageItemProps } from './SimilarProductImageItem.types';

const SimilarProductImageItem = ({
  imageUrl,
  isGeneratingBoxes,
  boxes,
  focusedBox,
  onBoxFocus,
  onBoxResize,
  onBoxResizeStop,
  isActive,
}: SimilarProductImageItemProps): JSX.Element => {
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const [imageSize, setImageSize] = useState<Dimensions>({
    width: 0,
    height: 0,
  });
  const [naturalSize, setNaturalSize] = useState<Dimensions>({
    width: 0,
    height: 0,
  });
  const [containerSize, setContainerSize] = useState<Dimensions>({
    width: 0,
    height: 0,
  });
  const [liveBox, setLiveBox] = useState<BoxDimensions | null>(null);
  const [isInteracting, setIsInteracting] = useState<boolean>(false);

  /**
   * Updates the size states based on the current image and container dimensions
   * Called on image load and window resize
   */
  const updateSize = useCallback(() => {
    if (!imageRef.current || !containerRef.current) return;

    const containerWidth = containerRef.current?.clientWidth || 1;
    const containerHeight = containerRef.current?.clientHeight || 1;
    const renderedWidth = imageRef.current.clientWidth;
    const renderedHeight = imageRef.current.clientHeight;
    const naturalWidth = imageRef.current.naturalWidth;
    const naturalHeight = imageRef.current.naturalHeight;

    if (
      containerWidth > 0 &&
      containerHeight > 0 &&
      renderedHeight > 0 &&
      renderedWidth > 0 &&
      naturalHeight > 0 &&
      naturalWidth > 0
    ) {
      setImageSize({ width: renderedWidth, height: renderedHeight });
      setContainerSize({ width: containerWidth, height: containerHeight });
      setNaturalSize({ width: naturalWidth, height: naturalHeight });
      setIsImageLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!imageRef.current || !containerRef.current) return;

    const currentImageRef = imageRef.current;
    const currentContainerRef = containerRef.current;

    const handleImageLoad = (): void => updateSize();

    const observer = new ResizeObserver(() => {
      updateSize();
    });

    // Add load event listener
    currentImageRef.addEventListener('load', handleImageLoad);
    observer.observe(currentContainerRef);

    if (imageRef.current.complete) {
      handleImageLoad();
    }

    return () => {
      if (currentImageRef) {
        currentImageRef.removeEventListener('load', handleImageLoad);
      }
      observer.disconnect();
    };
  }, [imageUrl, updateSize]);

  const scaleCoordinatesOfBox = useCallback(
    (x: number, y: number, width: number, height: number): BoxDimensions => {
      return scaleCoordinates(
        { x: x, y: y, width: width, height: height },
        imageSize,
        naturalSize,
        containerSize
      );
    },
    [imageSize, naturalSize, containerSize]
  );

  const reverseScaleCoordinatesOfBox = useCallback(
    (x: number, y: number, width: number, height: number): BoxDimensions => {
      return reverseScaleCoordinates(
        { x: x, y: y, width: width, height: height },
        imageSize,
        naturalSize,
        containerSize
      );
    },
    [imageSize, naturalSize, containerSize]
  );

  const handleBoxClick = useCallback(
    (event: React.MouseEvent, index: number) => {
      if (
        !isActive ||
        boxes === undefined ||
        focusedBox === null ||
        onBoxFocus === undefined ||
        isInteracting ||
        !containerRef.current
      )
        return;
      event.stopPropagation();

      // Get click coordinates
      const containerRect = containerRef.current.getBoundingClientRect();
      const clickX = event.clientX - containerRect.left;
      const clickY = event.clientY - containerRect.top;

      // Find all boxes that contain the click point
      const clickedBoxes = boxes.filter((box) => {
        if (box === focusedBox) {
          return false;
        }

        const scaledBox = scaleCoordinatesOfBox(
          box.x,
          box.y,
          box.width,
          box.height
        );

        return (
          clickX >= scaledBox.x &&
          clickX <= scaledBox.x + scaledBox.width &&
          clickY >= scaledBox.y &&
          clickY <= scaledBox.y + scaledBox.height
        );
      });

      if (clickedBoxes.length > 0) {
        // If clicking overlapping boxes
        if (clickedBoxes.length > 1) {
          // Find the box that's not currently selected
          const nonSelectedBox = clickedBoxes.find((box) => {
            const boxIndex = boxes.findIndex((b) => b === box);
            return (
              boxes[boxIndex].label !== focusedBox.label &&
              boxes[boxIndex].label !== 'Live Box'
            );
          });

          if (nonSelectedBox) {
            const newIndex = boxes.findIndex((b) => b === nonSelectedBox);
            onBoxFocus(newIndex);
          }
        } else {
          // If only one box is clicked
          onBoxFocus(index);
        }
      }
    },
    [
      onBoxFocus,
      isInteracting,
      boxes,
      focusedBox,
      scaleCoordinatesOfBox,
      isActive,
    ]
  );

  const handleResize = useCallback(
    (x: number, y: number, width: number, height: number) => {
      if (!isActive || onBoxResize === undefined) return;
      setIsInteracting(true);

      const unscaledCoordinates = reverseScaleCoordinatesOfBox(
        x,
        y,
        width,
        height
      );

      setLiveBox(unscaledCoordinates);
      onBoxResize();
    },
    [reverseScaleCoordinatesOfBox, onBoxResize, isActive]
  );

  const handleResizeStop = useCallback(
    (x: number, y: number, width: number, height: number) => {
      if (!isActive || onBoxResizeStop === undefined) return;
      const unscaledCoordinates = reverseScaleCoordinatesOfBox(
        x,
        y,
        width,
        height
      );

      onBoxResizeStop(unscaledCoordinates);
      setLiveBox(null);
      setIsInteracting(false);
    },
    [reverseScaleCoordinatesOfBox, onBoxResizeStop, isActive]
  );

  const shouldRenderBoxes =
    isActive &&
    !isGeneratingBoxes &&
    boxes &&
    boxes.length > 0 &&
    focusedBox &&
    isImageLoaded;
  const activeBox = shouldRenderBoxes ? liveBox || focusedBox : null;

  return (
    <div
      ref={containerRef}
      className={`${styles.imageContainer} ${isActive ? styles.active : styles.notActive}`}
    >
      <img
        ref={imageRef}
        src={imageUrl}
        alt="Product"
        className={styles.image}
      />

      {/* SVG mask for focused box */}
      {shouldRenderBoxes &&
        activeBox &&
        (() => {
          const scaledBox = scaleCoordinatesOfBox(
            activeBox.x,
            activeBox.y,
            activeBox.width,
            activeBox.height
          );

          return (
            <SvgBoxMask scaledBox={scaledBox} className={styles.svgOverlay} />
          );
        })()}

      {/* Bounding box indicators */}
      {shouldRenderBoxes &&
        boxes.map((box, index) => {
          if (isInteracting) return null;
          if (box.label === focusedBox.label) return null;
          if (box.baseLabel.toLowerCase() === 'person') return null;

          const scaledBox = scaleCoordinatesOfBox(
            box.x,
            box.y,
            box.width,
            box.height
          );

          const centerX = scaledBox.x + scaledBox.width / 2;
          const centerY = scaledBox.y + scaledBox.height / 2;

          return (
            <div
              key={index}
              className={styles.boxIndicator}
              style={{
                left: `${centerX - 4}px`,
                top: `${centerY - 4}px`,
              }}
              onClick={(e) => handleBoxClick(e, index)}
            />
          );
        })}

      {/* Invisible hitboxes for box selection */}
      {shouldRenderBoxes &&
        boxes.map((box, index) => {
          if (box === focusedBox) return null;

          const scaledBox = scaleCoordinatesOfBox(
            box.x,
            box.y,
            box.width,
            box.height
          );

          return (
            <div
              key={`hitbox-${index}`}
              className={styles.hitbox}
              style={{
                left: `${scaledBox.x}px`,
                top: `${scaledBox.y}px`,
                width: `${scaledBox.width}px`,
                height: `${scaledBox.height}px`,
              }}
              onClick={(e) => handleBoxClick(e, index)}
            />
          );
        })}

      {/* Rendering corners for box resize */}
      {shouldRenderBoxes &&
        activeBox &&
        (() => {
          const scaledBox = scaleCoordinatesOfBox(
            activeBox.x,
            activeBox.y,
            activeBox.width,
            activeBox.height
          );

          const cornerLength = 15;
          const cornerRadius = 8;
          const strokeWidth = 3;

          return (
            <>
              <Rnd
                size={{
                  width: scaledBox.width,
                  height: scaledBox.height,
                }}
                position={{ x: scaledBox.x, y: scaledBox.y }}
                bounds="parent"
                className={styles.rnd}
                resizeHandleClasses={{
                  bottom: styles.rndEdge,
                  top: styles.rndEdge,
                  left: styles.rndEdge,
                  right: styles.rndEdge,
                  bottomLeft: styles.rndCorner,
                  bottomRight: styles.rndCorner,
                  topLeft: styles.rndCorner,
                  topRight: styles.rndCorner,
                }}
                onResize={(_e, _dir, ref, _delta, position) => {
                  handleResize(
                    position.x,
                    position.y,
                    parseInt(ref.style.width),
                    parseInt(ref.style.height)
                  );
                }}
                onResizeStop={(_e, _dir, ref, _delta, position) => {
                  handleResizeStop(
                    position.x,
                    position.y,
                    parseInt(ref.style.width),
                    parseInt(ref.style.height)
                  );
                }}
              />

              <BoundingBoxHandles
                scaledBox={scaledBox}
                cornerLength={cornerLength}
                cornerRadius={cornerRadius}
                strokeWidth={strokeWidth}
                className={styles.bBoxHandles}
              />
            </>
          );
        })()}
    </div>
  );
};

export default SimilarProductImageItem;
